import { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  IconCrown,
  IconDiamond,
  IconGift,
  IconKey,
  IconShoppingBag,
} from '@tabler/icons-react'
import ScreenShell from './ScreenShell'
import { Button } from './figma'
import StarIcon from './StarIcon'
import { formatBalance } from '../lib/balance'
import { useIsMounted } from '../lib/useIsMounted'
import { getWebApp, telegramAuthHeaders } from '../lib/telegram'

const LIMIT = 20

const PRODUCT_TYPE_MAP = {
  stars: { label: 'Звёзды', Icon: StarIcon },
  premium: { label: 'Telegram Premium', Icon: IconCrown },
  ton: { label: 'TON', Icon: IconDiamond },
  archive_gift: { label: 'Подарок', Icon: IconGift },
  nft_rent: { label: 'Аренда подарка', Icon: IconGift },
  number_rent: { label: 'Аренда номера', Icon: IconKey },
  username_rent: { label: 'Аренда юзернейма', Icon: IconKey },
}

function pad2(value) {
  return String(value).padStart(2, '0')
}

function formatPurchaseDate(unixSec) {
  const date = new Date(Number(unixSec) * 1000)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }

  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

function getProductMeta(productType) {
  const key = String(productType || '').trim()
  if (PRODUCT_TYPE_MAP[key]) {
    return PRODUCT_TYPE_MAP[key]
  }

  return {
    label: key || 'Покупка',
    Icon: IconShoppingBag,
  }
}

function formatUnits(productType, amountUnits) {
  if (amountUnits == null || amountUnits === '') {
    return null
  }

  if (productType === 'stars') {
    return { text: String(amountUnits), star: true }
  }

  if (productType === 'premium') {
    return { text: `${amountUnits} мес`, star: false }
  }

  return null
}

function HistorySkeletonList() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }, (_, index) => (
        <div
          key={index}
          className="tma-card premium-card h-[72px] animate-pulse rounded-2xl"
        />
      ))}
    </div>
  )
}

function ButtonSpinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin text-[var(--color-text-secondary)]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

function HistoryScreen() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState(null)
  const isMountedRef = useIsMounted()
  const offsetRef = useRef(0)
  const loadingMoreRef = useRef(false)

  const loadPage = useCallback(async (reset = false) => {
    if (!reset && loadingMoreRef.current) {
      return
    }

    if (reset) {
      setLoading(true)
      setError(null)
    } else {
      loadingMoreRef.current = true
      setLoadingMore(true)
    }

    const requestOffset = reset ? 0 : offsetRef.current

    try {
      const tg = getWebApp()
      const params = new URLSearchParams({ offset: String(requestOffset), limit: String(LIMIT) })

      const response = await fetch(`/api/webapp-purchases?${params}`, { headers: telegramAuthHeaders(tg) })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.ok) {
        if (isMountedRef.current) {
          setError(String(result.error || 'Не удалось загрузить историю покупок'))
        }
        return
      }

      const pageItems = Array.isArray(result.items) ? result.items : []

      if (!isMountedRef.current) {
        return
      }

      if (reset) {
        setItems(pageItems)
        offsetRef.current = pageItems.length
      } else {
        setItems((currentItems) => [...currentItems, ...pageItems])
        offsetRef.current += pageItems.length
      }

      setHasMore(Boolean(result.has_more))
    } catch (loadError) {
      console.error('[HistoryScreen] load failed', loadError)
      if (isMountedRef.current) {
        setError('Не удалось загрузить историю покупок')
      }
    } finally {
      loadingMoreRef.current = false
      if (isMountedRef.current) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [isMountedRef])

  useEffect(() => {
    void loadPage(true)
  }, [loadPage])

  return (
    <ScreenShell title="История покупок">
      <div className="flow-screen-scroll relative min-h-0 w-full flex-1 space-y-3 overflow-y-auto pb-4">
        {loading ? (
          <HistorySkeletonList />
        ) : error ? (
          <div className="tma-card premium-card rounded-2xl px-4 py-5 text-center text-sm text-red-500">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="flex min-h-[240px] flex-col items-center justify-center px-6 text-center">
            <p className="text-[17px] font-medium text-[var(--color-text)]">Здесь пока пусто</p>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">Ваши покупки появятся тут</p>
          </div>
        ) : (
          <>
            {items.map((item, index) => {
              const { label, Icon } = getProductMeta(item.product_type)
              const units = formatUnits(item.product_type, item.amount_units)
              const dateLabel = formatPurchaseDate(item.created_at)

              return (
                <div
                  key={`${item.created_at}-${item.product_type}-${index}`}
                  className="tma-card premium-card flex items-center gap-3 rounded-2xl px-4 py-3"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Icon size={20} stroke={2} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-medium text-[var(--color-text)]">{label}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-[12px] text-[var(--tma-text-secondary)]">
                      <span>{dateLabel}</span>
                      {units ? (
                        <>
                          <span>·</span>
                          <span>{units.text}</span>
                          {units.star ? (
                            <StarIcon size={12} />
                          ) : null}
                        </>
                      ) : null}
                    </p>
                  </div>

                  <p className="shrink-0 text-[15px] font-semibold text-[var(--color-text)]">
                    {formatBalance(item.amount_rub)} ₽
                  </p>
                </div>
              )
            })}

            {hasMore ? (
              <Button
                type="button"
                variant="neutral"
                disabled={loadingMore}
                onClick={() => {
                  void loadPage(false)
                }}
                className="gap-2"
              >
                {loadingMore ? (
                  <>
                    <ButtonSpinner />
                    <span>Загрузка...</span>
                  </>
                ) : (
                  'Показать ещё'
                )}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </ScreenShell>
  )
}

export default memo(HistoryScreen)
